/**
 * TreasurePopup.jsx
 *
 * Renders the treasure-popup.html inside a full-screen iframe overlay.
 * The HTML file must be served as a static asset (e.g. in /public/treasure-popup.html).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { useSelector } from "react-redux"
import { selectAuth } from "shared/redux/slices/authSlice"
// ── Context ────────────────────────────────────────────────────────────────
const TreasureContext = createContext(null)

// ── Provider ───────────────────────────────────────────────────────────────
export function TreasureProvider({
  children,
  n8nUrl,
  userId,
  popupSrc = "/treasure-popup.html",
}) {
  const auth = useSelector(selectAuth)
  const resolvedUserId = userId ?? auth?.user?.userId ?? auth?.user?.id
  const [activeConfig, setActiveConfig] = useState(null)
  const [visible, setVisible] = useState(false)
  const iframeRef = useRef(null)

  const openPopup = useCallback((config) => {
    setActiveConfig({
      n8nUrl,
      userId: resolvedUserId,
      ...config,
    })
    setVisible(true)
  }, [n8nUrl, resolvedUserId])

  // Inject config into the iframe once it loads
  useEffect(() => {
    if (!visible || !activeConfig) return
    const iframe = iframeRef.current
    if (!iframe) return

    const inject = () => {
      try {
        if (iframe.contentWindow) {
          iframe.contentWindow.REWARD_CONFIG = {
            n8nUrl: activeConfig.n8nUrl,
            userId: activeConfig.userId,
            componentId: activeConfig.componentId,
            totalTreasures: 14,
          }
        }
      } catch {
        // cross-origin: serve popup HTML from same origin
      }
    }

    iframe.addEventListener("load", inject, { once: true })
    return () => iframe.removeEventListener("load", inject)
  }, [visible, activeConfig])

  // Listen for close message from iframe
  useEffect(() => {
    const handler = (e) => {
      if (e.data === "treasure-popup-close") {
        setVisible(false)
        setTimeout(() => setActiveConfig(null), 400)
      }
    }
    window.addEventListener("message", handler)
    return () => window.removeEventListener("message", handler)
  }, [])

  const iframeSrc = activeConfig
    ? `${popupSrc}?uid=${encodeURIComponent(
        activeConfig.userId
      )}&cid=${encodeURIComponent(
        activeConfig.componentId
      )}&n8n=${encodeURIComponent(activeConfig.n8nUrl)}`
    : ""

  return (
    <TreasureContext.Provider
      value={{ openPopup, n8nUrl, userId: resolvedUserId }}
    >
      {children}

      {visible && (
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          style={{
            position: "fixed",
            inset: 0,
            width: "100%",
            height: "100%",
            border: "none",
            zIndex: 99999,
            background: "transparent",
          }}
          allow="scripts"
          title="Treasure reward"
        />
      )}
    </TreasureContext.Provider>
  )
}

// ── Consumer hook (use inside any treasure component) ──────────────────────
export function useTreasureContext() {
  return useContext(TreasureContext)
}
